/**
 * Hangman Game — Main Container Component
 *
 * Orchestrates: difficulty/category selection → word generation → letter guessing →
 * win/loss detection → completion.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Keyboard support: type letters directly.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { HangmanEngine } from '../HangmanEngine';
import { HangmanBoard } from './HangmanBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { HangmanErrorHandler } from '../services/HangmanErrorHandler';
import { HangmanStatisticsService } from '../services/HangmanStatisticsService';
import { HangmanGamePersistence } from '../services/HangmanGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import UniversalGameLayout, { UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import {
  HangmanDifficulty,
  HangmanCategory,
  HangmanGameSettings,
  HangmanGameState,
  HangmanGameProps,
  MAX_WRONG_GUESSES,
} from '../types';
import './HangmanGame.css';

type GameView = 'mode-select' | 'playing' | 'completed';

const HangmanGameInner: React.FC<HangmanGameProps> = ({ onExit }) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<HangmanGameSettings | null>(null);
  const [gameState, setGameState] = useState<HangmanGameState | null>(null);
  const [timer, setTimer] = useState(0);

  // Refs for callbacks to avoid stale closures
  const engineRef = useRef<HangmanEngine>(new HangmanEngine());
  const gameStateRef = useRef<HangmanGameState | null>(null);
  const settingsRef = useRef<HangmanGameSettings | null>(null);
  const viewRef = useRef<GameView>('mode-select');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());

  // Services (lazy init)
  const errorHandlerRef = useRef<HangmanErrorHandler | null>(null);
  const statsServiceRef = useRef<HangmanStatisticsService | null>(null);
  const persistenceRef = useRef<HangmanGamePersistence | null>(null);

  // Keep refs in sync
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new HangmanErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new HangmanStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new HangmanGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Hangman services:', e);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Timer management
  useEffect(() => {
    if (view === 'playing' && gameState?.gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          const newTime = prev + 1;
          engineRef.current.updateTimer(newTime);
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [view, gameState?.gameStatus]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleGameComplete = useCallback(
    (state: HangmanGameState, won: boolean) => {
      setView('completed');

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      const currentSettings = settingsRef.current;
      if (statsServiceRef.current && currentSettings) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            difficulty: currentSettings.difficulty,
            category: currentSettings.category,
            won,
            word: state.word,
            guessCount: state.guessedLetters.length,
            wrongGuessCount: state.wrongGuessCount,
            hintUsed: state.hintUsed,
            time: state.timer,
            completedAt: Date.now(),
          },
          isAuthenticated ? user?.id : undefined
        );
      }

      if (won) {
        addNotification({
          type: NotificationType.SUCCESS,
          title: '🎉 You Won!',
          message: `You guessed "${state.word}" in ${formatTime(state.timer)}!`,
          duration: 5000,
        });
      } else {
        addNotification({
          type: NotificationType.WARNING,
          title: '💀 Game Over',
          message: `The word was "${state.word}". Better luck next time!`,
          duration: 5000,
        });
      }
    },
    [addNotification, isAuthenticated, user]
  );

  const handleStartGame = useCallback(
    (newSettings: HangmanGameSettings) => {
      const engine = engineRef.current;
      const state = engine.initializeGame(newSettings);

      setSettings(newSettings);
      setGameState(state);
      setTimer(0);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('hangman');

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.difficulty.charAt(0).toUpperCase() + newSettings.difficulty.slice(1)} difficulty, ${newSettings.category === 'all' ? 'all categories' : newSettings.category}. Good luck!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleGuessLetter = useCallback(
    (letter: string) => {
      const currentState = gameStateRef.current;
      if (!currentState || currentState.gameStatus !== 'playing') return;

      const engine = engineRef.current;

      try {
        const newState = engine.guessLetter(letter);
        setGameState(newState);

        // Auto-save
        const currentSettings = settingsRef.current;
        if (persistenceRef.current && currentSettings) {
          persistenceRef.current.autoSave(newState, {
            difficulty: currentSettings.difficulty,
            category: currentSettings.category,
            startedAt: newState.startedAt,
            lastSavedAt: Date.now(),
          });
        }

        // Check completion
        if (newState.gameStatus === 'won') {
          handleGameComplete(newState, true);
        } else if (newState.gameStatus === 'lost') {
          handleGameComplete(newState, false);
        }
      } catch (error) {
        console.warn('Hangman guess error:', error);
      }
    },
    [handleGameComplete]
  );

  const handleHint = useCallback(() => {
    const currentState = gameStateRef.current;
    if (!currentState || currentState.gameStatus !== 'playing') return;
    if (currentState.hintUsed) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Hint Already Used',
        message: 'You can only use one hint per game.',
        duration: 2000,
      });
      return;
    }

    const engine = engineRef.current;
    const { state: newState, letter } = engine.getHint();
    setGameState(newState);

    if (letter) {
      addNotification({
        type: NotificationType.INFO,
        title: '💡 Hint',
        message: `The letter "${letter}" has been revealed!`,
        duration: 2000,
      });

      if (newState.gameStatus === 'won') {
        handleGameComplete(newState, true);
      }
    }
  }, [addNotification, handleGameComplete]);

  const handleNewGame = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setView('mode-select');
    setGameState(null);
    setSettings(null);
    setTimer(0);
  }, []);

  const handlePlayAgain = useCallback(() => {
    // Start a new game with the same settings
    const currentSettings = settingsRef.current;
    if (currentSettings) {
      handleStartGame(currentSettings);
    } else {
      handleNewGame();
    }
  }, [handleStartGame, handleNewGame]);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Keyboard support
  // =========================================================================

  useEffect(() => {
    if (viewRef.current !== 'playing') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const currentState = gameStateRef.current;
      if (!currentState || currentState.gameStatus !== 'playing') return;

      const key = e.key.toUpperCase();

      // Letter keys A-Z
      if (/^[A-Z]$/.test(key)) {
        e.preventDefault();
        handleGuessLetter(key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, handleGuessLetter]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const hangmanConfig: GameModeConfig = {
      gameName: 'Hangman',
      gameIcon: '📝',
      availableModes: ['ai'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '🌱', description: 'Short words (4-5 letters)' },
        { id: 'medium', label: 'Medium', icon: '⭐', description: 'Medium words (6-8 letters)' },
        { id: 'hard', label: 'Hard', icon: '🏆', description: 'Long words (9+ letters)' },
      ],
      extraSections: [{
        title: 'Category',
        icon: '📂',
        options: [
          { id: 'all', label: 'All', icon: '🎲' },
          { id: 'animals', label: 'Animals', icon: '🐾' },
          { id: 'countries', label: 'Countries', icon: '🌍' },
          { id: 'foods', label: 'Foods', icon: '🍕' },
          { id: 'sports', label: 'Sports', icon: '⚽' },
          { id: 'technology', label: 'Technology', icon: '💻' },
          { id: 'movies', label: 'Movies', icon: '🎬' },
        ],
        defaultSelected: 'all',
        defaultOpen: true,
        visibleForModes: ['ai'],
      }],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      const difficulty = (selection.difficulty || 'medium') as HangmanDifficulty;
      const category = (selection.extras?.['extra-0'] || 'all') as HangmanCategory;
      handleStartGame({ difficulty, category });
    };

    return (
      <div className="hangman-game">
        <div className="hangman-game__header">
          <h2 className="hangman-game__title">📝 Hangman</h2>
        </div>
        <UniversalGameModeSelector
          config={hangmanConfig}
          onStart={handleModeSelection}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  if (!gameState) return null;

  // Left panels: Used letters
  const leftPanels = [
    {
      id: 'used-letters',
      title: 'Used Letters',
      icon: '🔤',
      content: (
        <div className="hangman-game__used-letters">
          {gameState.guessedLetters.map((letter) => {
            const isCorrect = gameState.word.toUpperCase().includes(letter);
            return (
              <span
                key={letter}
                className={`hangman-game__letter ${isCorrect ? 'hangman-game__letter--correct' : 'hangman-game__letter--wrong'}`}
              >
                {letter}
              </span>
            );
          })}
          {gameState.guessedLetters.length === 0 && (
            <span className="hangman-game__no-letters">No guesses yet</span>
          )}
        </div>
      ),
    },
  ];

  // Game over config
  const gameOverConfig = view === 'completed' && gameState ? {
    show: true,
    result: (gameState.gameStatus === 'won' ? 'win' : 'loss') as 'win' | 'loss',
    title: gameState.gameStatus === 'won' ? 'You Won!' : 'Game Over',
    subtitle: `The word was: ${gameState.word} | ${formatTime(timer)} | ${gameState.guessedLetters.length} guesses`,
    icon: gameState.gameStatus === 'won' ? '🎉' : '💀',
    onPlayAgain: handlePlayAgain,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Hangman',
    board: (
      <HangmanBoard
        gameState={gameState}
        onGuessLetter={handleGuessLetter}
        disabled={gameState.gameStatus !== 'playing'}
      />
    ),
    leftPanels,
    statusBar: {
      turnIndicator: (
        <span>
          {settings?.difficulty.charAt(0).toUpperCase()}{settings?.difficulty.slice(1)}
          {' | '}⏱️ {formatTime(timer)}
          {' | '}Guesses: {gameState.guessedLetters.length}
          {' | '}❌ {gameState.wrongGuessCount}/{MAX_WRONG_GUESSES}
        </span>
      ),
      gameStatus: gameState.gameStatus !== 'playing' ? (gameState.gameStatus === 'won' ? 'Won!' : 'Lost') : undefined,
      gameStatusType: gameState.gameStatus !== 'playing' ? 'ended' : 'active',
    },
    actions: [
      { id: 'new-game', label: 'New Word', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      {
        id: 'hint',
        label: gameState.hintUsed ? 'Hint Used' : 'Hint',
        icon: '💡',
        onClick: handleHint,
        disabled: gameState.hintUsed || gameState.gameStatus !== 'playing',
        variant: 'secondary',
      },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="hangman-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Format seconds as MM:SS */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Wrapped in ErrorBoundary */
const HangmanGame: React.FC<HangmanGameProps> = (props) => (
  <ErrorBoundary componentName="Hangman">
    <HangmanGameInner {...props} />
  </ErrorBoundary>
);

export { HangmanGame };
export default HangmanGame;
